import { useMemo } from 'react'
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays } from 'date-fns'
import { generateTaskOccurrences, isTaskDueOnDate } from '@/lib/recurrence'
import type { Task } from '@/types'

/**
 * Hook to generate task occurrences for a given date range
 */
export function useTaskOccurrences(
  tasks: Task[],
  date: Date,
  viewType: 'day' | 'week' | 'month' = 'month'
) {
  return useMemo(() => {
    let startDate: Date
    let endDate: Date

    switch (viewType) {
      case 'day':
        startDate = date
        endDate = addDays(date, 1)
        break
      case 'week':
        startDate = startOfWeek(date, { weekStartsOn: 0 })
        endDate = endOfWeek(date, { weekStartsOn: 0 })
        break
      case 'month':
      default:
        startDate = startOfMonth(date)
        endDate = endOfMonth(date)
        break
    }

    const occurrences: Array<{ task: Task; date: Date }> = []

    tasks.forEach((task) => {
      const taskOccurrences = generateTaskOccurrences(task, startDate, endDate)
      taskOccurrences.forEach((occurrenceDate) => {
        occurrences.push({ task, date: occurrenceDate })
      })
    })

    return occurrences.sort((a, b) => a.date.getTime() - b.date.getTime())
  }, [tasks, date, viewType])
}

/**
 * Hook to get tasks due today for a specific user
 */
export function useTodaysTasks(tasks: Task[], userId: string) {
  return useMemo(() => {
    const today = new Date()
    return tasks.filter(
      (task) => task.assigned_to === userId && isTaskDueOnDate(task, today)
    )
  }, [tasks, userId])
}

/**
 * Hook to get overdue tasks for a specific user
 */
export function useOverdueTasks(tasks: Task[], userId: string, completions: Array<{ task_id: string; completed_at: string }>) {
  return useMemo(() => {
    const now = new Date()
    const completedTaskIds = new Set(
      completions
        .filter((c) => new Date(c.completed_at) >= new Date(now.setHours(0, 0, 0, 0)))
        .map((c) => c.task_id)
    )

    return tasks.filter((task) => {
      if (task.assigned_to !== userId) return false
      if (completedTaskIds.has(task.id)) return false

      const taskDate = new Date(task.start_date)
      return taskDate < now && !completedTaskIds.has(task.id)
    })
  }, [tasks, userId, completions])
}
